import {
  APARTMENT_ROOM_OPTIONS,
  CASE_PRIORITIES,
  COOPERATION_TYPES,
  OWNER_SALE_SEQUENCE_END_REASONS,
  PROPERTY_TYPES,
  STAGES,
  TRANSACTION_TYPES,
  type ApartmentRooms,
  type CasePriority,
  type CaseLocationSource,
  type CaseStage,
  type CooperationType,
  type ListingFollowUpSequence,
  type DebriefAnalysis,
  type DebriefReminder,
  type MeetingDebrief,
  type OwnerSaleSequence,
  type OwnerSaleSequenceEndReason,
  type PropertyType,
  type RealEstateCase,
  type ShotScore,
  type TransactionType
} from "./types.ts";
import { normalizeMarketTimeline } from "./features/marketTimeline/domain.ts";
import { recoverDeterministicNextAction } from "./nextActionEngine.ts";
import { removeLegacyPhotographyAction } from "./listingPreparation.ts";
import { normalizePlannedActionCopy } from "./nextActionCopy.ts";
import { parseDebriefAnalysis } from "./debriefIntelligence.ts";

const STORAGE_KEY = "fispro-os:cases:v1";
const PENDING_DELETIONS_KEY = "fispro-os:case-deletions:v1";
const PENDING_WIPE_KEY = "fispro-os:case-wipe:v1";

export type PendingCaseDeletion = {
  id: string;
  deletedAt: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const normalizeStage = (stage: unknown): CaseStage => {
  if (stage === "Obhliadka" || stage === "Rokovanie") return "Aktívne";
  if (stage === "Uzavretý") return "Predané";
  return STAGES.includes(stage as CaseStage) ? (stage as CaseStage) : "Prvý cold call";
};

const normalizeTransactionType = (transactionType: unknown): TransactionType =>
  TRANSACTION_TYPES.includes(transactionType as TransactionType) ? (transactionType as TransactionType) : "Iné";

const normalizePropertyType = (propertyType: unknown): PropertyType | string => {
  if (typeof propertyType === "string" && propertyType.trim()) {
    return PROPERTY_TYPES.includes(propertyType as PropertyType) ? (propertyType as PropertyType) : propertyType;
  }

  return "Iné";
};

const normalizeApartmentRooms = (apartmentRooms: unknown): ApartmentRooms | undefined =>
  APARTMENT_ROOM_OPTIONS.includes(apartmentRooms as ApartmentRooms) ? (apartmentRooms as ApartmentRooms) : undefined;

const normalizePriority = (priority: unknown): CasePriority =>
  CASE_PRIORITIES.includes(priority as CasePriority) ? (priority as CasePriority) : "Bežný";

const normalizeCooperationType = (value: unknown): CooperationType =>
  COOPERATION_TYPES.includes(value as CooperationType) ? (value as CooperationType) : "unspecified";

const normalizePrice = (price: unknown) =>
  typeof price === "number" && Number.isFinite(price) ? price : 0;

const normalizeOptionalText = (value: unknown) =>
  typeof value === "string" && value.trim() ? value : undefined;

const normalizeLocationSource = (value: unknown): CaseLocationSource | undefined =>
  value === "dataset" || value === "custom" ? value : undefined;

const normalizePositiveNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;

const clampSequenceStep = (value: unknown, fallback: number) => {
  const step = typeof value === "number" && Number.isFinite(value) ? Math.round(value) : fallback;
  return Math.max(1, Math.min(4, step));
};

const normalizeOwnerSaleEndReason = (reason: unknown): OwnerSaleSequenceEndReason | undefined =>
  OWNER_SALE_SEQUENCE_END_REASONS.includes(reason as OwnerSaleSequenceEndReason)
    ? (reason as OwnerSaleSequenceEndReason)
    : undefined;

const normalizeOwnerSaleSequence = (sequence: unknown): OwnerSaleSequence | undefined => {
  if (!isRecord(sequence)) {
    return undefined;
  }

  const completedContacts =
    typeof sequence.completedContacts === "number" && Number.isFinite(sequence.completedContacts)
      ? Math.max(0, Math.min(4, Math.round(sequence.completedContacts)))
      : 0;
  const status = sequence.status === "Ukončená" ? "Ukončená" : "Aktívna";

  return {
    status,
    currentStep: clampSequenceStep(sequence.currentStep, Math.min(4, completedContacts + 1)),
    completedContacts,
    endReason: status === "Ukončená" ? normalizeOwnerSaleEndReason(sequence.endReason) : undefined,
    endedAt: typeof sequence.endedAt === "string" ? sequence.endedAt : undefined,
    lastResult: typeof sequence.lastResult === "string" ? sequence.lastResult : undefined,
    lastNote: typeof sequence.lastNote === "string" ? sequence.lastNote : undefined
  };
};

const normalizeListingFollowUpSequence = (sequence: unknown): ListingFollowUpSequence | undefined => {
  if (!isRecord(sequence)) return undefined;
  const status = sequence.status === "Dokončená" || sequence.status === "Ukončená" ? sequence.status : "Aktívna";
  return {
    status,
    currentStep: sequence.currentStep === 3 ? 3 : sequence.currentStep === 2 ? 2 : 1,
    firstFollowUpCompletedAt: normalizeOptionalText(sequence.firstFollowUpCompletedAt),
    secondFollowUpCompletedAt: normalizeOptionalText(sequence.secondFollowUpCompletedAt),
    lastMarketReviewCompletedAt: normalizeOptionalText(sequence.lastMarketReviewCompletedAt),
    endedAt: normalizeOptionalText(sequence.endedAt),
    endReason: normalizeOptionalText(sequence.endReason)
  };
};

const normalizeShotScores = (value: unknown): ShotScore[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const scores = value.flatMap((item): ShotScore[] => {
    if (!isRecord(item) || typeof item.id !== "string" || typeof item.activityId !== "string") return [];
    const score = [0, 25, 50, 75, 100].includes(item.score as number) ? item.score as ShotScore["score"] : 0;
    return [{
      id: item.id,
      caseId: typeof item.caseId === "string" ? item.caseId : "",
      activityId: item.activityId,
      activityType: item.activityType === "viewing" ? "viewing" : "meeting",
      identity: item.identity === true,
      value: item.value === true,
      acquisition: item.acquisition === true,
      nextStep: item.nextStep === true,
      score,
      completedAt: typeof item.completedAt === "string" ? item.completedAt : new Date(0).toISOString()
    }];
  });
  return scores.length > 0 ? scores : undefined;
};

const normalizeDebriefAnalysis = (value: unknown): DebriefAnalysis | undefined => {
  try {
    return parseDebriefAnalysis(value);
  } catch {
    return undefined;
  }
};

const normalizeDebriefReminders = (value: unknown): DebriefReminder[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const allowedStatuses = ["scheduled", "pending", "snoozed", "analyzing", "review", "completed", "not_required"];
  const allowedTypes = ["property_viewing", "acquisition_meeting", "client_meeting", "financial_meeting", "negotiation", "follow_up_meeting"];
  const reminders = value.flatMap((item): DebriefReminder[] => {
    if (!isRecord(item) || typeof item.id !== "string" || typeof item.eventId !== "string" || !allowedTypes.includes(String(item.eventType))) return [];
    return [{
      ...(item as unknown as DebriefReminder),
      status: allowedStatuses.includes(String(item.status)) ? item.status as DebriefReminder["status"] : "pending",
      analysis: normalizeDebriefAnalysis(item.analysis)
    }];
  });
  return reminders.length ? reminders : undefined;
};

const normalizeMeetingDebriefs = (value: unknown): MeetingDebrief[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const debriefs = value.flatMap((item): MeetingDebrief[] => {
    if (!isRecord(item) || typeof item.id !== "string" || typeof item.reminderId !== "string") return [];
    const analysis = normalizeDebriefAnalysis(item.analysis);
    if (!analysis) return [];
    return [{ ...(item as unknown as MeetingDebrief), analysis }];
  });
  return debriefs.length ? debriefs : undefined;
};

const normalizeCase = (caseItem: unknown): RealEstateCase | null => {
  if (!isRecord(caseItem)) {
    return null;
  }

  const now = new Date().toISOString();
  const stage = normalizeStage(caseItem.stage);
  const legacyStatus = typeof caseItem.status === "string" ? caseItem.status.trim() : "";
  const status = legacyStatus === "Obhliadka" || legacyStatus === "Rokovanie" || legacyStatus === "Uzavretý"
    ? (stage as RealEstateCase["status"])
    : legacyStatus
      ? (legacyStatus as RealEstateCase["status"])
      : stage;
  const legacyEstimatedPrice = normalizePrice(caseItem.estimatedPrice);

  return {
    id: typeof caseItem.id === "string" ? caseItem.id : `case-${Date.now()}`,
    contactName: typeof caseItem.contactName === "string" ? caseItem.contactName : "",
    phone: typeof caseItem.phone === "string" ? caseItem.phone : "",
    email: typeof caseItem.email === "string" ? caseItem.email : "",
    transactionType: normalizeTransactionType(caseItem.transactionType),
    propertyAddress: typeof caseItem.propertyAddress === "string" ? caseItem.propertyAddress : "",
    city: normalizeOptionalText(caseItem.city),
    cityNormalized: normalizeOptionalText(caseItem.cityNormalized),
    citySource: normalizeLocationSource(caseItem.citySource),
    municipalityId: normalizeOptionalText(caseItem.municipalityId),
    street: normalizeOptionalText(caseItem.street),
    streetNormalized: normalizeOptionalText(caseItem.streetNormalized),
    streetSource: normalizeLocationSource(caseItem.streetSource),
    streetId: normalizeOptionalText(caseItem.streetId),
    locality: normalizeOptionalText(caseItem.locality),
    propertyType: normalizePropertyType(caseItem.propertyType),
    apartmentRooms: normalizePropertyType(caseItem.propertyType) === "Byt" ? normalizeApartmentRooms(caseItem.apartmentRooms) : undefined,
    area: normalizePositiveNumber(caseItem.area),
    cooperationType: normalizeCooperationType(caseItem.cooperationType),
    askingPrice: normalizePrice(caseItem.askingPrice),
    recommendedPrice:
      typeof caseItem.recommendedPrice === "number" && Number.isFinite(caseItem.recommendedPrice)
        ? caseItem.recommendedPrice
        : legacyEstimatedPrice,
    estimatedPrice:
      typeof caseItem.estimatedPrice === "number" && Number.isFinite(caseItem.estimatedPrice)
        ? caseItem.estimatedPrice
        : undefined,
    commissionPotential: typeof caseItem.commissionPotential === "number" ? caseItem.commissionPotential : 0,
    actualCommission: typeof caseItem.actualCommission === "number" ? caseItem.actualCommission : undefined,
    listingStartedAt: normalizeOptionalText(caseItem.listingStartedAt),
    reservationUntil: normalizeOptionalText(caseItem.reservationUntil),
    initialListingPrice: normalizePositiveNumber(caseItem.initialListingPrice),
    realizedPrice: normalizePositiveNumber(caseItem.realizedPrice),
    soldAt: normalizeOptionalText(caseItem.soldAt),
    status,
    stage,
    leadState:
      caseItem.leadState === "Čakajúci" || caseItem.leadState === "Vyradený" || caseItem.leadState === "Aktívny"
        ? caseItem.leadState
        : undefined,
    waitingSince: normalizeOptionalText(caseItem.waitingSince),
    excludedAt: normalizeOptionalText(caseItem.excludedAt),
    exclusionReason: normalizeOptionalText(caseItem.exclusionReason),
    lastBusinessStage: STAGES.includes(caseItem.lastBusinessStage as RealEstateCase["stage"])
      ? caseItem.lastBusinessStage as RealEstateCase["stage"]
      : undefined,
    contactCountAtExclusion:
      typeof caseItem.contactCountAtExclusion === "number" && Number.isFinite(caseItem.contactCountAtExclusion)
        ? caseItem.contactCountAtExclusion
        : undefined,
    priority: normalizePriority(caseItem.priority),
    ownerSaleSequence: normalizeOwnerSaleSequence(caseItem.ownerSaleSequence),
    listingFollowUpSequence: normalizeListingFollowUpSequence(caseItem.listingFollowUpSequence),
    shotScores: normalizeShotScores(caseItem.shotScores),
    debriefReminders: normalizeDebriefReminders(caseItem.debriefReminders),
    meetingDebriefs: normalizeMeetingDebriefs(caseItem.meetingDebriefs),
    marketTimeline: normalizeMarketTimeline(caseItem.marketTimeline),
    nextStep:
      typeof caseItem.nextStep === "string" && caseItem.nextStep.trim()
        ? caseItem.nextStep
        : caseItem.followUp
          ? "Follow-up"
          : "Chýba ďalší krok",
    followUp: isRecord(caseItem.followUp)
      ? {
          date: typeof caseItem.followUp.date === "string" ? caseItem.followUp.date : "",
          time: typeof caseItem.followUp.time === "string" ? caseItem.followUp.time : "",
          ...(normalizeOptionalText(caseItem.followUp.endTime) ? { endTime: normalizeOptionalText(caseItem.followUp.endTime) } : {}),
          purpose: typeof caseItem.followUp.purpose === "string"
            ? normalizePlannedActionCopy(caseItem.followUp.purpose)
            : "",
          workflowId:
            caseItem.followUp.workflowId === "listing" || caseItem.followUp.workflowId === "reservation"
              ? caseItem.followUp.workflowId
              : undefined,
          actionId:
            caseItem.followUp.actionId === "advertising_report" ||
            caseItem.followUp.actionId === "market_review" ||
            caseItem.followUp.actionId === "market_review_recurring" ||
            caseItem.followUp.actionId === "reservation_expiry"
              ? caseItem.followUp.actionId
              : undefined
        }
      : undefined,
    closedAt: typeof caseItem.closedAt === "string" ? caseItem.closedAt : undefined,
    closingNote: typeof caseItem.closingNote === "string" ? caseItem.closingNote : undefined,
    lostReason: typeof caseItem.lostReason === "string" ? caseItem.lostReason : undefined,
    closedLostAt: normalizeOptionalText(caseItem.closedLostAt),
    notes: typeof caseItem.notes === "string" ? caseItem.notes : "",
    timeline: Array.isArray(caseItem.timeline) ? (caseItem.timeline as RealEstateCase["timeline"]) : [],
    createdAt: typeof caseItem.createdAt === "string" ? caseItem.createdAt : now,
    updatedAt: typeof caseItem.updatedAt === "string" ? caseItem.updatedAt : now
  };
};

export const normalizeCases = (value: unknown): RealEstateCase[] =>
  Array.isArray(value)
    ? value
        .map(normalizeCase)
        .filter((caseItem): caseItem is RealEstateCase => Boolean(caseItem))
        .map(removeLegacyPhotographyAction)
        .map((caseItem) => recoverDeterministicNextAction(caseItem))
    : [];

export const loadCases = (): RealEstateCase[] => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }

    return normalizeCases(JSON.parse(stored));
  } catch {
    return [];
  }
};

export const saveCases = (cases: RealEstateCase[]) => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cases));
};

export const clearCases = () => {
  window.localStorage.removeItem(STORAGE_KEY);
};

export const loadPendingCaseDeletions = (): PendingCaseDeletion[] => {
  try {
    const stored = window.localStorage.getItem(PENDING_DELETIONS_KEY);
    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(
      (item): item is PendingCaseDeletion =>
        isRecord(item) && typeof item.id === "string" && typeof item.deletedAt === "string"
    );
  } catch {
    return [];
  }
};

export const queueCaseDeletions = (caseIds: string[], deletedAt = new Date().toISOString()) => {
  if (caseIds.length === 0) {
    return;
  }

  const pendingById = new Map(loadPendingCaseDeletions().map((item) => [item.id, item]));
  caseIds.forEach((id) => pendingById.set(id, { id, deletedAt }));
  window.localStorage.setItem(PENDING_DELETIONS_KEY, JSON.stringify([...pendingById.values()]));
};

export const clearPendingCaseDeletions = (caseIds: string[]) => {
  const clearedIds = new Set(caseIds);
  const remaining = loadPendingCaseDeletions().filter((item) => !clearedIds.has(item.id));

  if (remaining.length === 0) {
    window.localStorage.removeItem(PENDING_DELETIONS_KEY);
    return;
  }

  window.localStorage.setItem(PENDING_DELETIONS_KEY, JSON.stringify(remaining));
};

export const queueAllCaseDeletions = (deletedAt = new Date().toISOString()) => {
  window.localStorage.setItem(PENDING_WIPE_KEY, deletedAt);
};

export const loadPendingCaseWipe = () => window.localStorage.getItem(PENDING_WIPE_KEY);

export const clearPendingCaseWipe = () => {
  window.localStorage.removeItem(PENDING_WIPE_KEY);
};
